import React, { useRef, useState, useEffect, useCallback } from 'react';

// Интерфейс для данных линии (ОСТАВЛЕН ОРИГИНАЛЬНЫЙ)
interface LineData {
    id: number;
    d: string; // Путь SVG
    opacity: number;
    width: number;
    // Добавлены внутренние параметры для анимации, чтобы избежать изменения LineData
    animDuration?: number; 
    animDelay?: number;
}

// Интерфейс для стилей SVG path
interface SVGPathStyle extends React.CSSProperties {
    strokeWidth?: string | number;
    strokeDashoffset?: string | number;
    strokeDasharray?: string | number;
}

// --- КОНСТАНТЫ АНИМАЦИИ (СКОРРЕКТИРОВАННЫЕ) ---
const NUM_LINES = 12; 

// Диапазоны анимации, привязанные к прогрессу скролла (от 0 до 1)
const ANIM_START = 0.25;        // Линии начинают появляться
const ANIM_END = 0.5;          // Линии полностью прорисованы
const FADE_OUT_START = 1;      // Линии начинают исчезать с 90%

const TRACE_VISIBLE_RANGE = ANIM_END - ANIM_START; 


// --- ЦВЕТОВЫЕ ХЕЛПЕРЫ ДЛЯ ИНТЕРПОЛЯЦИИ ---
// Теперь используем переменные для определения START и END цветов!
// В CSS-файлах эти цвета определены как:
// --brand-secondary: #1c1c1c (Начало фона)
// --brand-primary: #F5300D (Конец фона)
// --brand-dark: #130C0F (Начало текста)
// --brand-white: #f0f0f0 (Конец текста)

// !!! Важно: В React компоненте у нас нет прямого доступа к :root переменным DOM, 
// поэтому для логики *динамической интерполяции* мы временно используем 
// жесткие HEX-коды, но *финальный* цвет будем применять через инлайн-стиль. 
// Однако, для статических стилей (как цвет линий) мы можем использовать var().
const BG_START_HEX = '#1c1c1c'; 
const BG_END_HEX = '#F5300D';   
const TEXT_START_HEX = '#130C0F'; 
const TEXT_END_HEX = '#f0f0f0'; 
const COLOR_ANIM_RANGE = 0.25; 

const hexToRgb = (hex: string): [number, number, number] => {
    const bigint = parseInt(hex.substring(1), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return [r, g, b];
};

const componentToHex = (c: number): string => {
    const hex = Math.round(c).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
};

const rgbToHex = (r: number, g: number, b: number): string => {
    return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
};

const lerp = (start: number, end: number, progress: number): number => {
    return start + (end - start) * progress;
};

const interpolateColor = (startHex: string, endHex: string, progress: number): string => {
    const [startR, startG, startB] = hexToRgb(startHex);
    const [endR, endG, endB] = hexToRgb(endHex);

    const r = lerp(startR, endR, progress);
    const g = lerp(startG, endG, progress);
    const b = lerp(startB, endB, progress);

    return rgbToHex(r, g, b);
};
// --------------------------------------------------------------------------------


// 📌 ГЕНЕРАЦИЯ 12 ВОЛНИСТЫХ ЛИНИЙ (СИНУСОИД)
const generateSineWaves = (count: number): LineData[] => {
    const lines: LineData[] = [];
    const cx = 50; 
    const cy = 50; 
    const maxRadius = 75; 
    const segments = 50; 

    for (let i = 0; i < count; i++) {
        const angle = (i / count) * Math.PI * 2;
        let d = `M ${cx} ${cy}`;

        for (let j = 1; j <= segments; j++) {
            const t = j / segments;
            const currentRadius = t * maxRadius;
            const offset = Math.sin(t * 12) * 3 * t;

            const x = cx + (Math.cos(angle) * currentRadius) - (Math.sin(angle) * offset);
            const y = cy + (Math.sin(angle) * currentRadius) + (Math.cos(angle) * offset);

            d += ` L ${x.toFixed(2)} ${y.toFixed(2)}`;
        }

        lines.push({
            id: i,
            d: d,
            opacity: 1, 
            width: 5.0,   
            animDuration: 0.5 + Math.random() * 0.5, 
            animDelay: Math.random() * 0.5,
        });
    }
    return lines;
};

const LINES_DATA: LineData[] = generateSineWaves(NUM_LINES);


const VisualMagnet: React.FC = () => {
    const containerRef = useRef<HTMLElement>(null);
    const [scrollYProgress, setScrollYProgress] = useState(0); 
    const [isVisible, setIsVisible] = useState(false);

    // 1. ОТСЛЕЖИВАНИЕ ВИДИМОСТИ СЕКЦИИ
    useEffect(() => {
        const element = containerRef.current;
        if (!element) return;

        const observer = new IntersectionObserver(
            (entries) => {
                setIsVisible(entries[0].isIntersecting);
            },
            { rootMargin: '0px', threshold: 0 } 
        );

        observer.observe(element);
        
        return () => observer.disconnect();
    }, []);

    // 2. ОТСЛЕЖИВАНИЕ ПРОГРЕССА СКРОЛЛА
    const handleScroll = useCallback(() => {
        if (!containerRef.current || !isVisible) return;

        const container = containerRef.current;
        const scrollPosition = window.scrollY;
        
        const rect = container.getBoundingClientRect();
        const sectionTop = rect.top + window.scrollY;
        const sectionHeight = rect.height;
        
        const startPoint = sectionTop - window.innerHeight; 
        const endPoint = sectionTop + sectionHeight; 
        
        const totalScrollRange = endPoint - startPoint;
        const scrolledDistance = scrollPosition - startPoint;

        // Нормированный прогресс от 0 до 1
        let progress = Math.max(0, Math.min(1, scrolledDistance / totalScrollRange));
        
        setScrollYProgress(progress);
    }, [isVisible]);

    useEffect(() => {
        if (isVisible) {
            window.addEventListener('scroll', handleScroll, { passive: true });
            handleScroll(); 
        } else {
            window.removeEventListener('scroll', handleScroll);
        }
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isVisible, handleScroll]);


    // 3. РАСЧЕТ ЦВЕТОВ КРУГА
    const normalize = (value: number, min: number, max: number): number => {
        return Math.max(0, Math.min(1, (value - min) / (max - min)));
    };
    
    const colorProgress = normalize(scrollYProgress, 0, COLOR_ANIM_RANGE);
    // Применяем сглаживание (Cubic ease-in-out) для более плавной анимации
    const smoothedProgress = colorProgress * colorProgress * (3 - 2 * colorProgress);

    // Динамическая интерполяция цвета (приходится использовать инлайн-стиль)
    const bgColor = interpolateColor(BG_END_HEX, BG_END_HEX, smoothedProgress);
    const textColor = interpolateColor(TEXT_END_HEX, TEXT_END_HEX, smoothedProgress);
    

    return (
        <section 
            ref={containerRef}
            id="visual-magnet"
            // 📌 ИЗМЕНЕНИЕ 1: Используем bg-brand-secondary из globals.css для начального состояния 
            // (хотя оно будет перебито инлайн-стилем в процессе анимации)
            className="bg-brand-secondary relative h-screen w-full flex items-center justify-center overflow-hidden sticky top-0"
        >
            
            {/* --- SVG: ВОЛНИСТЫЕ ЛИНИИ С АНИМАЦИЕЙ --- */}
            <svg 
                className="absolute inset-0 w-full h-full pointer-events-none" 
                viewBox="0 0 100 100" 
                preserveAspectRatio="xMidYMid slice"
                xmlns="http://www.w3.org/2000/svg"
            >
                {LINES_DATA.map((line) => {
                    // 1. АКТИВНАЯ ФАЗА ПРОРИСОВКИ 
                    let traceProgress = Math.max(0, 
                        Math.min(1, (scrollYProgress - ANIM_START) / TRACE_VISIBLE_RANGE)
                    );
                    
                    const lineAnimEnd = line.animDuration! + line.animDelay!;
                    const finalTraceProgress = Math.max(0, 
                        Math.min(1, traceProgress / lineAnimEnd)
                    );
                    
                    const dasharray = '1.0'; 
                    let finalDashoffset = 1.0 - finalTraceProgress; 

                    // 2. ФАЗА ИСЧЕЗНОВЕНИЯ 
                    const fadeProgress = Math.max(0, 
                        (scrollYProgress - FADE_OUT_START) / (1 - FADE_OUT_START)
                    );
                    
                    let currentOpacity = line.opacity;
                    let currentWidth = 0; 

                    if (fadeProgress > 0) {
                        finalDashoffset = finalDashoffset + fadeProgress * 1.5; 
                        currentOpacity = line.opacity * Math.max(0, 1 - fadeProgress); 
                        currentWidth = line.width * Math.max(0, 1 - fadeProgress); 
                    } else {
                        currentWidth = line.width * finalTraceProgress;
                    }
                    
                    // 3. ФАЗА ПОЯВЛЕНИЯ
                    if (scrollYProgress <= ANIM_START) {
                        currentOpacity = line.opacity * (scrollYProgress / ANIM_START);
                    }
                    
                    currentOpacity = Math.max(0, Math.min(line.opacity, currentOpacity));
                    currentWidth = Math.max(0, Math.min(line.width, currentWidth));


                    return (
                        <path 
                            key={line.id} 
                            d={line.d} 
                            fill="none"
                            // 📌 ИЗМЕНЕНИЕ 2: Используем CSS-переменную для цвета обводки
                            stroke="var(--brand-primary)" 
                            strokeLinecap="round"
                            pathLength="1" 
                            
                            style={{ 
                                strokeWidth: currentWidth, 
                                strokeDasharray: dasharray, 
                                strokeDashoffset: finalDashoffset, 
                                opacity: currentOpacity,
                            } as SVGPathStyle} 
                        />
                    );
                })}
            </svg>

            {/* --- ЦЕНТРАЛЬНЫЙ КРУГ С ТЕКСТОМ (Анимация цвета, масштаба и прозрачности) --- */}
            <div className="relative z-10 p-4 pointer-events-none">
                <div 
                    style={{backgroundColor: bgColor}}
                    // 📌 ИЗМЕНЕНИЕ 3: Используем CSS-переменные для размеров круга (фиксированные w/h, p, m/p-side, p-medium/p-big)
                    // Для размеров w-72/h-72/w-96/h-96 нет прямых переменных в globals.css,
                    // но используются переменные отступов, например, --indent-big (32px на моб, 48px на md). 
                    // Оставляем w/h как есть, но используем отступы.
                    className="w-72 h-72 md:w-96 md:h-96 rounded-full flex items-center justify-center p-8 md:p-12 text-center"
                >
                    <h2 
                        style={{
                            // 📌 ИЗМЕНЕНИЕ 4: Динамический цвет текста (необходимо для анимации)
                            color: textColor 
                        }}
                        // 📌 ИЗМЕНЕНИЕ 5: Используем классы типографики из globals.css 
                        className="display-xl leading-[0.9] tracking-tight"
                    >
                        Научим тебя двигаться
                    </h2>
                </div>
            </div>

        </section>
    );
};

export default VisualMagnet;